import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCreativeCommons, FaLanguage, FaBell, FaEnvelope, FaSms, FaWindowRestore, FaCog, FaGripVertical, FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdBrandingWatermark } from 'react-icons/md';
import { TbCalendarEvent } from 'react-icons/tb';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/check_login';

const SECTION_KEYS = ['statuses', 'recentActivity', 'quickStats', 'creativeTypes'];

const SECTION_LABELS = {
  statuses: 'Creatives by Status',
  recentActivity: 'Recent Activity',
  quickStats: 'Quick Stats Cards',
  creativeTypes: 'Creative Types Breakdown'
};

const ALL_STAT_CARDS = ['creatives', 'languages', 'brands', 'campaigns'];

const DEFAULT_SECTIONS = {
  statuses: { visible: true, order: 1, selectedItems: [], width: 'full' },
  recentActivity: { visible: true, order: 2, selectedItems: [], width: 'full' },
  quickStats: { visible: true, order: 3, selectedCards: ALL_STAT_CARDS, width: 'full' },
  creativeTypes: { visible: true, order: 4, selectedItems: [], width: 'full' }
};

// Saved layout merged with the defaults, so new sections show up and broken data cannot crash the page
const loadSections = () => {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem('dashboardSections') || 'null');
  } catch (e) {
    saved = null;
  }
  const merged = SECTION_KEYS.map(key => [key, { ...DEFAULT_SECTIONS[key], ...(saved && typeof saved[key] === 'object' ? saved[key] : {}) }]);
  merged.sort((a, b) => (a[1].order ?? 99) - (b[1].order ?? 99));
  return Object.fromEntries(merged.map(([key, value], index) => [key, { ...value, order: index + 1 }]));
};

// Items shown in a filterable section: an empty selection means "all"
const filterSelected = (items, selectedIds, getId) => {
  if (!selectedIds || selectedIds.length === 0) return items;
  const filtered = items.filter(item => selectedIds.includes(getId(item)));
  return filtered.length > 0 ? filtered : items;
};

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [builderMode, setBuilderMode] = useState(false);
  const [draggedSection, setDraggedSection] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);

  // Dashboard sections visibility
  const [sections, setSections] = useState(loadSections);

  useEffect(() => {
    localStorage.setItem('dashboardSections', JSON.stringify(sections));
  }, [sections]);

  const toggleSection = (sectionName) => {
    setSections(prev => ({
      ...prev,
      [sectionName]: { ...prev[sectionName], visible: !prev[sectionName].visible }
    }));
  };

  const getSortedSections = () => Object.entries(sections)
    .filter(([key]) => SECTION_KEYS.includes(key))
    .sort((a, b) => a[1].order - b[1].order);

  const getSortedKeys = () => getSortedSections().map(([key]) => key);

  const applyOrder = (orderedKeys) => {
    setSections(prev => Object.fromEntries(
      Object.entries(prev).map(([key, value]) => [key, { ...value, order: orderedKeys.indexOf(key) + 1 }])
    ));
  };

  const moveSection = (sectionName, direction) => {
    const keys = getSortedKeys();
    const index = keys.indexOf(sectionName);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= keys.length) return;
    [keys[index], keys[swapIndex]] = [keys[swapIndex], keys[index]];
    applyOrder(keys);
  };

  // Toggles one item of a filterable section. Keeps at least one item - hide the section to show none.
  const toggleItemSelection = (sectionName, itemId, allIds) => {
    setSections(prev => {
      const section = prev[sectionName];
      const current = section.selectedItems?.length
        ? section.selectedItems.filter(id => allIds.includes(id))
        : allIds;
      const next = current.includes(itemId) ? current.filter(id => id !== itemId) : [...current, itemId];
      if (next.length === 0) return prev;
      return {
        ...prev,
        [sectionName]: { ...section, selectedItems: next.length === allIds.length ? [] : next }
      };
    });
  };

  const isItemShown = (sectionName, itemId) => {
    const selected = sections[sectionName]?.selectedItems || [];
    return selected.length === 0 || selected.includes(itemId);
  };

  const toggleCardSelection = (cardName) => {
    setSections(prev => {
      const section = prev.quickStats;
      const selectedCards = section.selectedCards || ALL_STAT_CARDS;
      const newSelectedCards = selectedCards.includes(cardName)
        ? selectedCards.filter(c => c !== cardName)
        : ALL_STAT_CARDS.filter(c => c === cardName || selectedCards.includes(c));
      if (newSelectedCards.length === 0) return prev;
      return {
        ...prev,
        quickStats: { ...section, selectedCards: newSelectedCards }
      };
    });
  };

  const resetDashboard = () => {
    setSections(DEFAULT_SECTIONS);
  };

  const setWidthForSection = (sectionKey, width) => {
    setSections(prev => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], width }
    }));
  };

  const handleDragStart = (e, sectionKey) => {
    setDraggedSection(sectionKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionKey);
  };

  const handleDragOver = (e, sectionKey) => {
    if (!draggedSection) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(sectionKey);
  };

  const handleDragEnd = () => {
    setDraggedSection(null);
    setDraggedOver(null);
  };

  // Moves the dragged section to the target position (after it when moving down, before it when moving up)
  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    const dragged = draggedSection;
    setDraggedSection(null);
    setDraggedOver(null);
    if (!dragged || dragged === targetKey) return;

    const keys = getSortedKeys();
    const movingDown = keys.indexOf(dragged) < keys.indexOf(targetKey);
    const remaining = keys.filter(key => key !== dragged);
    remaining.splice(remaining.indexOf(targetKey) + (movingDown ? 1 : 0), 0, dragged);
    applyOrder(remaining);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/dashboard/stats', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboardData(res.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
  }, []);
  
  // Sample data - replace with real data from your API
  const [dashboardData, setDashboardData] = useState({
    creatives: { total: 0, recent: 0, types: [] },
    languages: { active: 0, inactive: 0 },
    brands: { total: 0, recent: 0 },
    campaigns: { total: 0, active: 0, byStatus: [] }
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [statusData, setStatusData] = useState({ enabled: false, statuses: [] });

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/dashboard/recent-creatives', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecentActivities(
          res.data.map(item => ({
            ...item,
            time: new Date(item.time).toLocaleString() // или format със date-fns/moment
          }))
        );
      } catch (err) {
        console.error('Failed to fetch recent creatives:', err);
      }
    };
  
    fetchRecent();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/dashboard/statuses', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatusData(res.data);
      } catch (err) {
        console.error('Failed to fetch status data:', err);
      }
    };

    fetchStatuses();
  }, []);

  const navigateTo = (path) => {
    navigate(path);
  };

  const renderSectionContent = (sectionName) => {
    switch(sectionName) {
      case 'statuses':
        const statusesToShow = filterSelected(statusData.statuses, sections.statuses.selectedItems, s => s._id);

        return statusData.enabled && statusData.statuses.length > 0 && (
          <div key="statuses" className="status-section">
            <h2 style={{ marginBottom: '1rem' }}>Creatives by Status</h2>
            <div className="status-grid">
              {statusesToShow.map((status) => (
              <motion.div
                key={status._id}
                className="status-card"
                style={{
                  '--status-button-bg': status.color,
                  borderRadius: '8px',
                  padding: '1.5rem',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #ddd'
                }}
                whileHover={{ y: -5, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        backgroundColor: status.color 
                      }}
                    />
                    {status.name}
                  </h3>
                  <span 
                    className="badge" 
                    style={{ 
                      backgroundColor: status.color, 
                      color: '#fff', 
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    {status.total}
                  </span>
                </div>
                
                {status.creatives.length > 0 ? (
                  <>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0' }}>
                      {status.creatives.map((creative) => (
                        <li 
                          key={creative.id}
                          style={{
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.9rem',
                            cursor: (user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? 'pointer' : 'not-allowed',
                            opacity: (user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? 1 : 0.6
                          }}
                          onClick={() => (user?.role?.permissions?.canViewLibrary || user?.isAdmin) && creative.availableLanguages?.length > 0 && navigate(`/creative/edit/${creative.availableLanguages[0]}/${creative.id}`)}
                          title={(user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? '' : 'No permission to view'}
                        >
                          <div style={{ fontWeight: '500', color: '#333' }}>{creative.title}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            <span className={`text-uppercase type-badge activity-type ${creative.type?.toLowerCase() || 'default'}`} style={{ marginRight: '0.5rem' }}>
                              {creative.type}
                            </span>
                            <span>{new Date(creative.createdAt).toLocaleDateString()}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {(user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? (
                      <button
                        className="action-button"
                        onClick={() => navigate('/library/creatives', { state: { filterByStatus: status._id } })}
                      >
                        See All
                      </button>
                    ) : (
                      <button
                        className="action-button"
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                        disabled
                        title="No permission to view library"
                      >
                        No Access
                      </button>
                    )}
                  </>
                ) : (
                  <p style={{ color: '#999', textAlign: 'center', padding: '1rem 0' }}>No creatives with this status</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
        );

      case 'recentActivity':
        return (
          <div key="recentActivity" className="recent-activity" style={{ marginBottom: '2rem' }}>
            <h2>Recent Activity</h2>
            <ul>
              {recentActivities.map(activity => (
                <motion.li key={activity.id} whileHover={{ x: 5 }}>
                  <span className={`activity-type ${activity.type.toLowerCase()}`}>
                    {activity.type}
                  </span>
                  <span className="activity-action">{activity.action}</span>
                  <span className="activity-name">
                    {activity.availableLanguages?.length > 0 ? (
                      (user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? (
                        <span
                          onClick={() =>
                            navigate(`/view/${activity.availableLanguages[0]}/${activity.id}`)
                          }
                          className="preview-link"
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#3cbec6' }}
                        >
                          {activity.name}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>{activity.name}</span>
                      )
                    ) : (
                      <span>{activity.name}</span>
                    )}
                  </span>
                  <span className="activity-time">{activity.time}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        );

      case 'quickStats':
        const selectedCards = sections.quickStats.selectedCards || ['creatives', 'languages', 'brands', 'campaigns'];
        return (
          <div key="quickStats" className="stats-grid">
            {selectedCards.includes('creatives') && (
              <motion.div 
                className="stat-card creative-card"
                whileHover={{ y: -5 }}
              >
                <div className="card-icon">
                  <FaCreativeCommons />
                </div>
                <div className="card-content">
                  <h3>{dashboardData.creatives.total}</h3>
                  <p>Total Creatives</p>
                  <small>+{dashboardData.creatives.recent} recent</small>
                  {(user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%' }}
                      onClick={() => navigate('/library/creatives')}
                    >
                      View Library
                    </button>
                  ) : (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%', opacity: 0.5, cursor: 'not-allowed' }}
                      disabled
                      title="No permission to view library"
                    >
                      No Access
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {selectedCards.includes('languages') && (
              <motion.div 
                className="stat-card language-card"
                whileHover={{ y: -5 }}
              >
                <div className="card-icon">
                  <FaLanguage />
                </div>
                <div className="card-content">
                  <h3>{dashboardData.languages.active}</h3>
                  <p>Active Languages</p>
                  <small>{dashboardData.languages.inactive} inactive</small>
                  {(user?.role?.permissions?.canManageLanguages || user?.isAdmin) ? (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%' }}
                      onClick={() => navigate('/language')}
                    >
                      Manage Languages
                    </button>
                  ) : (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%', opacity: 0.5, cursor: 'not-allowed' }}
                      disabled
                      title="No permission to manage languages"
                    >
                      No Access
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {selectedCards.includes('brands') && (
              <motion.div 
                className="stat-card brand-card"
                whileHover={{ y: -5 }}
              >
                <div className="card-icon">
                  <MdBrandingWatermark />
                </div>
                <div className="card-content">
                  <h3>{dashboardData.brands.total}</h3>
                  <p>Total Brands</p>
                  <small>+{dashboardData.brands.recent} new</small>
                  {(user?.role?.permissions?.canManageBrands || user?.isAdmin) ? (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%' }}
                      onClick={() => navigate('/brand/create')}
                    >
                      Manage Brands
                    </button>
                  ) : (
                    <button 
                      className="action-button" 
                      style={{ marginTop: '0.75rem', width: '100%', opacity: 0.5, cursor: 'not-allowed' }}
                      disabled
                      title="No permission to manage brands"
                    >
                      No Access
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {selectedCards.includes('campaigns') && (
              <motion.div
                className="stat-card campaign-card"
                whileHover={{ y: -5 }}
              >
                <div className="card-icon">
                  <TbCalendarEvent />
                </div>
                <div className="card-content">
                  <h3>{dashboardData.campaigns.total}</h3>
                  <p>Marketing Campaigns</p>
                  <small>{dashboardData.campaigns.active} active</small>
                  {(user?.role?.permissions?.canManageCampaigns || user?.isAdmin) ? (
                    <button
                      className="action-button"
                      style={{ marginTop: '0.75rem', width: '100%' }}
                      onClick={() => navigate('/marketing-calendar')}
                    >
                      View Calendar
                    </button>
                  ) : (
                    <button
                      className="action-button"
                      style={{ marginTop: '0.75rem', width: '100%', opacity: 0.5, cursor: 'not-allowed' }}
                      disabled
                      title="No permission to manage campaigns"
                    >
                      No Access
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        );

      case 'creativeTypes':
        const typesToShow = filterSelected(dashboardData.creatives.types, sections.creativeTypes.selectedItems, t => t.name);

        return (
          <div key="creativeTypes" className="creative-types">
            <h2>Creative Types Breakdown</h2>
            <div className="type-grid">
              {typesToShow.map((type, index) => (
                <motion.div 
                  key={index}
                  className="type-card"
                  style={{ borderTop: `4px solid ${type.color}` }}
                  whileHover={(user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? { scale: 1.03 } : {}}
                  onClick={() => (user?.role?.permissions?.canViewLibrary || user?.isAdmin) && navigate('/library/creatives', { state: { filterByCreativeType: type.name } })}
                >
                  <div className="type-icon" style={{ color: type.color }}>
                    {type.icon}
                  </div>
                  <h3>{type.name}</h3>
                  <p>{type.count} creatives</p>
                  {(user?.role?.permissions?.canViewLibrary || user?.isAdmin) ? (
                    <button className="action-button">View</button>
                  ) : (
                    <button 
                      className="action-button" 
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                      disabled
                      title="No permission to view library"
                    >
                      No Access
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSection = (sectionName, index, total) => {
    const section = sections[sectionName];
    if (!section) return null;
    const width = section.width || 'full';
    const content = renderSectionContent(sectionName);

    if (!builderMode) {
      if (!section.visible || !content) return null;
      return (
        <div key={sectionName} className={`dashboard-section width-${width}`}>
          {content}
        </div>
      );
    }

    // Builder: every section (hidden ones too) gets a toolbar above its content
    const hidden = !section.visible;
    const emptyMessage = sectionName === 'statuses' && !statusData.enabled
      ? 'The status system is disabled - enable it in Settings → Statuses to show this section.'
      : 'Nothing to show yet.';

    return (
      <div
        key={sectionName}
        className={[
          'dashboard-section',
          `width-${width}`,
          'builder',
          hidden ? 'is-hidden' : '',
          draggedSection === sectionName ? 'is-dragging' : '',
          draggedOver === sectionName && draggedSection !== sectionName ? 'is-drag-over' : ''
        ].join(' ')}
        draggable
        onDragStart={(e) => handleDragStart(e, sectionName)}
        onDragOver={(e) => handleDragOver(e, sectionName)}
        onDragEnd={handleDragEnd}
        onDrop={(e) => handleDrop(e, sectionName)}
      >
        <div className="builder-section-frame">
          <div className="builder-section-toolbar">
            <FaGripVertical className="builder-section-grip" title="Drag to move" />
            <strong className="builder-section-title">{SECTION_LABELS[sectionName]}</strong>
            {hidden && <span className="builder-section-badge">Hidden</span>}
            <div className="builder-section-actions">
              <button type="button" onClick={() => toggleSection(sectionName)} title={hidden ? 'Show on dashboard' : 'Hide from dashboard'}>
                {hidden ? <><FaEye /> Show</> : <><FaEyeSlash /> Hide</>}
              </button>
              <button type="button" onClick={() => moveSection(sectionName, 'up')} disabled={index === 0} title="Move up">↑</button>
              <button type="button" onClick={() => moveSection(sectionName, 'down')} disabled={index === total - 1} title="Move down">↓</button>
              <select value={width} onChange={(e) => setWidthForSection(sectionName, e.target.value)} title="Width">
                <option value="full">Full width</option>
                <option value="two-thirds">Two thirds</option>
                <option value="half">Half</option>
                <option value="third">Third</option>
              </select>
            </div>
          </div>
          <div className="builder-section-body">
            {content || <p className="builder-section-empty">{emptyMessage}</p>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="creative-dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <h1>Content Craft Dashboard</h1>
          <p>Welcome back! Here's what's happening with your content.</p>
        </div>
        <div className="dashboard-header-actions">
          <button 
            className={`layout-builder-btn ${builderMode ? "primary-button" : "secondary-button"}`}
            onClick={() => setBuilderMode(!builderMode)}
          >
            <FaGripVertical /> {builderMode ? 'Exit Builder' : 'Layout Builder'}
          </button>
          <button 
            className="secondary-button" 
            onClick={() => setShowSettings(!showSettings)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FaCog /> Settings
          </button>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="dashboard-settings">
          <div className="settings-header">
            <h3>Customize Dashboard Sections</h3>
            <button 
              className="secondary-button" 
              onClick={resetDashboard}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Reset to Default
            </button>
          </div>
          <div className="settings-sections">
            {getSortedSections().map(([key, value], index) => (
              <div 
                key={key}
                className="settings-section-item"
                draggable
                onDragStart={(e) => handleDragStart(e, key)}
                onDragOver={(e) => handleDragOver(e, key)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, key)}
                style={{
                  background: draggedOver === key ? '#e3f2fd' : '#f9f9f9',
                  opacity: draggedSection === key ? 0.5 : 1,
                  border: draggedOver === key ? '2px dashed #2196f3' : '1px solid transparent'
                }}>
                <div className="settings-section-header">
                  <div className="settings-section-left">
                    <FaGripVertical style={{ color: '#999', cursor: 'grab' }} />
                    <input
                      type="checkbox"
                      checked={value.visible}
                      onChange={() => toggleSection(key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span style={{ fontWeight: '500' }}>
                      {SECTION_LABELS[key]}
                      {key === 'statuses' && !statusData.enabled && (
                        <small style={{ fontWeight: 400, color: '#999', marginLeft: '0.5rem' }}>(status system disabled)</small>
                      )}
                    </span>
                  </div>
                  <div className="settings-section-controls">
                    <button
                      className="action-button"
                      onClick={(e) => { e.stopPropagation(); moveSection(key, 'up'); }}
                      disabled={index === 0}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                    >
                      ↑
                    </button>
                    <button
                      className="action-button"
                      onClick={(e) => { e.stopPropagation(); moveSection(key, 'down'); }}
                      disabled={index === getSortedSections().length - 1}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                    >
                      ↓
                    </button>
                  </div>
                </div>

                {/* Statuses multiselect */}
                {key === 'statuses' && value.visible && statusData.statuses.length > 0 && (
                  <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem', color: '#666' }}>
                      Select statuses to display:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {statusData.statuses.map(status => (
                        <label key={status._id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                          <input
                            type="checkbox"
                            checked={isItemShown('statuses', status._id)}
                            disabled={isItemShown('statuses', status._id) && filterSelected(statusData.statuses, value.selectedItems, s => s._id).length === 1}
                            onChange={() => toggleItemSelection('statuses', status._id, statusData.statuses.map(s => s._id))}
                          />
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: status.color }} />
                            {status.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Stats Cards multiselect */}
                {key === 'quickStats' && value.visible && (
                  <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem', color: '#666' }}>
                      Select cards to display:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={value.selectedCards?.includes('creatives') ?? true}
                          disabled={value.selectedCards?.length === 1 && value.selectedCards[0] === 'creatives'}
                          onChange={() => toggleCardSelection('creatives')}
                        />
                        Total Creatives
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={value.selectedCards?.includes('languages') ?? true}
                          disabled={value.selectedCards?.length === 1 && value.selectedCards[0] === 'languages'}
                          onChange={() => toggleCardSelection('languages')}
                        />
                        Active Languages
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={value.selectedCards?.includes('brands') ?? true}
                          disabled={value.selectedCards?.length === 1 && value.selectedCards[0] === 'brands'}
                          onChange={() => toggleCardSelection('brands')}
                        />
                        Brands
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={value.selectedCards?.includes('campaigns') ?? true}
                          disabled={value.selectedCards?.length === 1 && value.selectedCards[0] === 'campaigns'}
                          onChange={() => toggleCardSelection('campaigns')}
                        />
                        Marketing Campaigns
                      </label>
                    </div>
                  </div>
                )}

                {/* Creative Types multiselect */}
                {key === 'creativeTypes' && value.visible && dashboardData.creatives.types.length > 0 && (
                  <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem', color: '#666' }}>
                      Select creative types to display:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {dashboardData.creatives.types.map(type => (
                        <label key={type.name} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
                          <input
                            type="checkbox"
                            checked={isItemShown('creativeTypes', type.name)}
                            disabled={isItemShown('creativeTypes', type.name) && filterSelected(dashboardData.creatives.types, value.selectedItems, t => t.name).length === 1}
                            onChange={() => toggleItemSelection('creativeTypes', type.name, dashboardData.creatives.types.map(t => t.name))}
                          />
                          {type.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Builder Mode Info */}
      {builderMode && (
        <div className="builder-mode-info">
          <span>
            <strong>🎨 Layout Builder</strong> - drag sections or use ↑ ↓ to reorder, Show / Hide to choose what is visible,
            and set the width (side-by-side layouts apply on large screens). Changes are saved automatically in this browser.
          </span>
          <span className="builder-mode-actions">
            <button className="secondary-button" onClick={resetDashboard}>Reset to Default</button>
            <button className="primary-button" onClick={() => setBuilderMode(false)}>Done</button>
          </span>
        </div>
      )}

      {/* Render sections in custom order with flex wrap */}
      <div className="dashboard-sections">
        {getSortedSections().map(([key], index, all) => renderSection(key, index, all.length))}
      </div>
    </div>
  );
};

export default Home;